import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ResultCard } from "./pipe-helpers";

/* ═══════════════════════════════════════════════════════
   STAR PATTERN BOLT TIGHTENING DIAGRAM
   ═══════════════════════════════════════════════════════ */

const getStarSequence = (n: number): number[] => {
  if (n <= 0) return [];
  const seq: number[] = [];
  const used = new Set<number>();
  let pos = 0;
  for (let i = 0; i < n; i++) {
    let target = (pos) % n;
    while (used.has(target)) {
      target = (target + 1) % n;
    }
    seq.push(target);
    used.add(target);
    pos = (target + Math.floor(n / 2)) % n;
  }
  return seq;
};

const StarPatternDiagram = ({ boltCount }: { boltCount: number }) => {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const sequence = useMemo(() => getStarSequence(boltCount), [boltCount]);

  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const flangeR = 85;
  const boltR = 68;
  const boltDotR = 10;

  const bolts = Array.from({ length: boltCount }, (_, i) => {
    const angle = (i / boltCount) * Math.PI * 2 - Math.PI / 2;
    return { x: cx + boltR * Math.cos(angle), y: cy + boltR * Math.sin(angle) };
  });

  const lines: { x1: number; y1: number; x2: number; y2: number; step: number }[] = [];
  for (let i = 0; i < sequence.length - 1; i++) {
    const from = bolts[sequence[i]];
    const to = bolts[sequence[i + 1]];
    lines.push({ x1: from.x, y1: from.y, x2: to.x, y2: to.y, step: i });
  }

  const isHighlighted = (boltIdx: number) => {
    if (activeStep === null) return false;
    return sequence.indexOf(boltIdx) <= activeStep;
  };

  const getStepNumber = (boltIdx: number) => sequence.indexOf(boltIdx) + 1;

  return (
    <div className="trade-card mt-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        Star Pattern — {boltCount} Bolts
      </h4>
      <div className="flex justify-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="select-none">
          <circle cx={cx} cy={cy} r={flangeR} fill="none" className="stroke-border" strokeWidth={2} />
          <circle cx={cx} cy={cy} r={flangeR - 20} fill="none" className="stroke-border" strokeWidth={1} strokeDasharray="4 4" />
          {lines.map((line, i) => {
            const visible = activeStep === null || i < (activeStep ?? 0);
            return (
              <line key={i} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
                className={visible ? "stroke-primary" : "stroke-muted"}
                strokeWidth={visible ? 1.5 : 0.5}
                strokeDasharray={visible ? "none" : "3 3"}
                opacity={visible ? 0.6 : 0.2}
              />
            );
          })}
          {bolts.map((b, i) => {
            const highlighted = activeStep === null || isHighlighted(i);
            const isCurrent = activeStep !== null && sequence[activeStep] === i;
            return (
              <g key={i} className="cursor-pointer" onClick={() => {
                const step = sequence.indexOf(i);
                setActiveStep(step === activeStep ? null : step);
              }}>
                <circle cx={b.x} cy={b.y} r={boltDotR}
                  className={isCurrent ? "fill-primary stroke-primary" : highlighted ? "fill-primary/80 stroke-primary" : "fill-muted stroke-border"}
                  strokeWidth={isCurrent ? 2.5 : 1.5}
                />
                <text x={b.x} y={b.y} textAnchor="middle" dominantBaseline="central"
                  className={`text-[9px] font-bold select-none ${highlighted ? "fill-primary-foreground" : "fill-muted-foreground"}`}>
                  {getStepNumber(i)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex justify-center gap-3 mt-2">
        <button
          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors underline"
          onClick={() => setActiveStep(activeStep === null ? 0 : activeStep > 0 ? activeStep - 1 : null)}
        >
          ◀ Prev
        </button>
        <span className="text-[10px] text-muted-foreground">
          {activeStep !== null ? `Step ${activeStep + 1} of ${boltCount}` : "Tap a bolt or step through"}
        </span>
        <button
          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors underline"
          onClick={() => setActiveStep(activeStep === null ? 0 : activeStep < boltCount - 1 ? activeStep + 1 : null)}
        >
          Next ▶
        </button>
      </div>
      <p className="text-[10px] text-muted-foreground text-center mt-1">
        Pass 1: 30% → Pass 2: 70% → Pass 3: 100% → Final: verify
      </p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   FLANGE TORQUE CALCULATOR
   ═══════════════════════════════════════════════════════ */

const FLANGE_SIZES = [
  "1/2\"","3/4\"","1\"","1-1/4\"","1-1/2\"","2\"","2-1/2\"","3\"","4\"","5\"","6\"","8\"","10\"","12\"","14\"","16\"","18\"","20\"","24\""
];

const FLANGE_BOLTING: Record<string, Record<string, { count: number; dia: number }>> = {
  '1/2"':   { "150": {count:4, dia:0.5},   "300": {count:4, dia:0.5},   "600": {count:4, dia:0.625}, "900": {count:4, dia:0.75},  "1500": {count:4, dia:0.875}, "2500": {count:4, dia:0.875} },
  '3/4"':   { "150": {count:4, dia:0.5},   "300": {count:4, dia:0.625}, "600": {count:4, dia:0.75},  "900": {count:4, dia:0.75},  "1500": {count:4, dia:1.0},   "2500": {count:4, dia:1.0} },
  '1"':     { "150": {count:4, dia:0.5},   "300": {count:4, dia:0.625}, "600": {count:4, dia:0.75},  "900": {count:4, dia:0.875}, "1500": {count:4, dia:1.125}, "2500": {count:4, dia:1.125} },
  '1-1/4"': { "150": {count:4, dia:0.5},   "300": {count:4, dia:0.625}, "600": {count:4, dia:0.75},  "900": {count:4, dia:0.875}, "1500": {count:4, dia:1.125}, "2500": {count:4, dia:1.25} },
  '1-1/2"': { "150": {count:4, dia:0.5},   "300": {count:4, dia:0.75},  "600": {count:4, dia:0.875}, "900": {count:4, dia:1.0},   "1500": {count:4, dia:1.25},  "2500": {count:4, dia:1.375} },
  '2"':     { "150": {count:4, dia:0.625}, "300": {count:8, dia:0.625}, "600": {count:8, dia:0.625}, "900": {count:8, dia:0.75},  "1500": {count:8, dia:0.875}, "2500": {count:8, dia:1.0} },
  '2-1/2"': { "150": {count:4, dia:0.625}, "300": {count:8, dia:0.75},  "600": {count:8, dia:0.875}, "900": {count:8, dia:1.0},   "1500": {count:8, dia:1.0},   "2500": {count:8, dia:1.125} },
  '3"':     { "150": {count:4, dia:0.625}, "300": {count:8, dia:0.75},  "600": {count:8, dia:0.875}, "900": {count:8, dia:0.875}, "1500": {count:8, dia:1.125}, "2500": {count:8, dia:1.25} },
  '4"':     { "150": {count:8, dia:0.625}, "300": {count:8, dia:0.75},  "600": {count:8, dia:1.0},   "900": {count:8, dia:1.125}, "1500": {count:8, dia:1.375}, "2500": {count:8, dia:1.5} },
  '5"':     { "150": {count:8, dia:0.75},  "300": {count:8, dia:0.75},  "600": {count:8, dia:1.0},   "900": {count:8, dia:1.25},  "1500": {count:8, dia:1.5} },
  '6"':     { "150": {count:8, dia:0.75},  "300": {count:12,dia:0.75},  "600": {count:12,dia:1.0},   "900": {count:12,dia:1.125}, "1500": {count:12,dia:1.375}, "2500": {count:8, dia:2.0} },
  '8"':     { "150": {count:8, dia:0.75},  "300": {count:12,dia:0.875}, "600": {count:12,dia:1.125}, "900": {count:12,dia:1.375}, "1500": {count:12,dia:1.625}, "2500": {count:12,dia:1.75} },
  '10"':    { "150": {count:12,dia:0.875}, "300": {count:16,dia:1.0},   "600": {count:16,dia:1.25},  "900": {count:16,dia:1.375}, "1500": {count:12,dia:2.0},   "2500": {count:12,dia:2.0} },
  '12"':    { "150": {count:12,dia:0.875}, "300": {count:16,dia:1.125}, "600": {count:20,dia:1.25},  "900": {count:20,dia:1.375}, "1500": {count:16,dia:2.0},   "2500": {count:12,dia:2.5} },
  '14"':    { "150": {count:12,dia:1.0},   "300": {count:20,dia:1.125}, "600": {count:20,dia:1.375}, "900": {count:20,dia:1.5},   "1500": {count:16,dia:2.25} },
  '16"':    { "150": {count:16,dia:1.0},   "300": {count:20,dia:1.25},  "600": {count:20,dia:1.5},   "900": {count:20,dia:1.75},  "1500": {count:16,dia:2.5} },
  '18"':    { "150": {count:16,dia:1.125}, "300": {count:24,dia:1.25},  "600": {count:20,dia:1.625}, "900": {count:20,dia:2.0},   "1500": {count:16,dia:2.75} },
  '20"':    { "150": {count:20,dia:1.125}, "300": {count:24,dia:1.25},  "600": {count:24,dia:1.625}, "900": {count:24,dia:2.0},   "1500": {count:16,dia:3.0} },
  '24"':    { "150": {count:20,dia:1.25},  "300": {count:24,dia:1.5},   "600": {count:24,dia:1.875}, "900": {count:24,dia:2.25},  "1500": {count:16,dia:3.5} },
};

const GASKET_BOLT_STRESS: Record<string, number> = {
  "spiral_wound": 25000,
  "flat_nonasbestos": 15000,
  "rtj": 30000,
  "ptfe": 10000,
  "rubber": 8000,
};

const GASKET_OPTIONS = [
  { value: "spiral_wound", label: "Spiral Wound (CGI)" },
  { value: "flat_nonasbestos", label: "Flat Non-Asbestos" },
  { value: "rtj", label: "Ring Joint (RTJ)" },
  { value: "ptfe", label: "PTFE / Teflon" },
  { value: "rubber", label: "Full-Face Rubber" },
];

const LUBE_OPTIONS = [
  { value: "dry", label: "Dry / Unlubricated", kFactor: 0.20 },
  { value: "moly", label: "Moly Paste (MoS2)", kFactor: 0.12 },
  { value: "antiseize", label: "Anti-Seize (Copper/Nickel)", kFactor: 0.15 },
  { value: "oil", label: "Machine Oil", kFactor: 0.17 },
  { value: "ptfe_tape", label: "PTFE Tape on Threads", kFactor: 0.14 },
];

const CLASS_OPTIONS = ["150", "300", "600", "900", "1500", "2500"];

export const FlangeTorqueCalculator = () => {
  const [sizeIdx, setSizeIdx] = useState("5");
  const [pressClass, setPressClass] = useState("150");
  const [gasketType, setGasketType] = useState("spiral_wound");
  const [lubeIdx, setLubeIdx] = useState("0");

  const result = useMemo(() => {
    const size = FLANGE_SIZES[parseInt(sizeIdx)];
    const bolting = FLANGE_BOLTING[size]?.[pressClass];
    if (!bolting) return null;

    const lube = LUBE_OPTIONS[parseInt(lubeIdx)];
    const targetStress = GASKET_BOLT_STRESS[gasketType] ?? 25000;
    const boltArea = Math.PI * (bolting.dia / 2) ** 2 * 0.7854 / (Math.PI * (bolting.dia / 2) ** 2) * Math.PI * (bolting.dia * 0.85 / 2) ** 2;
    const tensileArea = 0.7854 * (bolting.dia - 0.9743 / (1 / (bolting.dia * 8))) ** 2;
    const tpi = bolting.dia <= 0.5 ? 13 : bolting.dia <= 0.625 ? 11 : bolting.dia <= 0.75 ? 10 : bolting.dia <= 0.875 ? 9 : bolting.dia <= 1.0 ? 8 : bolting.dia <= 1.125 ? 7 : bolting.dia <= 1.25 ? 7 : bolting.dia <= 1.5 ? 6 : bolting.dia <= 1.75 ? 5 : bolting.dia <= 2.0 ? 4.5 : bolting.dia <= 2.5 ? 4 : 4;
    const pitch = 1 / tpi;
    const stressArea = 0.7854 * (bolting.dia - 0.9743 * pitch) ** 2;

    const boltLoad = targetStress * stressArea;
    const totalLoad = boltLoad * bolting.count;
    const torque = lube.kFactor * boltLoad * bolting.dia;
    const torqueFtLb = torque / 12;

    // Suppress unused variable warnings
    void boltArea;
    void tensileArea;

    return {
      size,
      boltCount: bolting.count,
      boltDia: bolting.dia,
      stressArea,
      boltLoad,
      totalLoad,
      torqueFtLb,
      torqueInLb: torque,
      kFactor: lube.kFactor,
      tpi,
    };
  }, [sizeIdx, pressClass, gasketType, lubeIdx]);

  const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">{children}</label>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Pipe Size</FieldLabel>
          <Select value={sizeIdx} onValueChange={setSizeIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FLANGE_SIZES.map((s, i) => (
                <SelectItem key={i} value={String(i)}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Pressure Class</FieldLabel>
          <Select value={pressClass} onValueChange={setPressClass}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CLASS_OPTIONS.map(c => (
                <SelectItem key={c} value={c}>{c}#</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <FieldLabel>Gasket Type</FieldLabel>
        <Select value={gasketType} onValueChange={setGasketType}>
          <SelectTrigger className="bg-card border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {GASKET_OPTIONS.map(g => (
              <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <FieldLabel>Lubricant / Condition</FieldLabel>
        <Select value={lubeIdx} onValueChange={setLubeIdx}>
          <SelectTrigger className="bg-card border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LUBE_OPTIONS.map((l, i) => (
              <SelectItem key={i} value={String(i)}>
                <span>{l.label}</span>
                <span className="ml-2 text-[10px] text-muted-foreground">K={l.kFactor}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {result ? (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Target Torque" value={fmt(result.torqueFtLb, 0)} unit="ft-lb" accent />
          <ResultCard label="Target Torque" value={fmt(result.torqueInLb, 0)} unit="in-lb" />
          <ResultCard label="Bolt Load (each)" value={fmt(result.boltLoad, 0)} unit="lbs" />
          <ResultCard label="Total Clamp Load" value={fmt(result.totalLoad, 0)} unit="lbs" />
          <div className="trade-card">
            <div className="grid grid-cols-2 gap-y-1.5 text-xs">
              <span className="text-muted-foreground">Bolt Count</span>
              <span className="font-mono-data text-right">{result.boltCount}× {result.boltDia}" dia</span>
              <span className="text-muted-foreground">Thread (UNC)</span>
              <span className="font-mono-data text-right">{result.tpi} TPI</span>
              <span className="text-muted-foreground">Stress Area</span>
              <span className="font-mono-data text-right">{fmt(result.stressArea, 4)} in²</span>
              <span className="text-muted-foreground">Nut Factor (K)</span>
              <span className="font-mono-data text-right">{result.kFactor}</span>
            </div>
          </div>
          <StarPatternDiagram boltCount={result.boltCount} />
          <p className="text-[10px] text-muted-foreground leading-snug mt-2">
            Based on ASME PCC-1 guidance. Use star pattern, 3-pass minimum. Values are per-bolt targets — verify against gasket manufacturer specs for critical service.
          </p>
        </div>
      ) : (
        <div className="trade-card text-center text-sm text-muted-foreground">
          No bolt data for this size/class combination
        </div>
      )}
    </div>
  );
};
